import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import CustomChatRoom from '../../common/components/CustomChatRoom/CustomChatRoom';
import {
  DiscussionMessageType,
  SendMessageToDiscussionInput,
} from '../../types/graphql-global-types';
import {
  DiscussionMessages,
  DiscussionMessages_discussionMessages_messages,
  DiscussionMessages_discussionMessages_messages_business,
  DiscussionMessages_discussionMessages_messages_isSeenByUsers,
  DiscussionMessages_discussionMessages_messages_quotedFrom,
  DiscussionMessagesVariables,
} from '../../graphql/discussion/types/DiscussionMessages';
import {
  ApiErrorCode,
  DiscussionMessageTag,
  Operation,
  Resource,
} from '../../common/constants';
import {
  displaySnackBar,
  SnackVariableInterface,
} from '../../common/displaySnackbar';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription,
} from '@apollo/react-hooks';
import {
  GET_ALL_MESSAGE_UNSEEN,
  GET_DISCUSSION_MESSAGE,
  GET_DISCUSSIONS,
} from '../../graphql/discussion/query';
import {
  SendMessageToDiscussion,
  SendMessageToDiscussionVariables,
} from '../../graphql/discussion/types/SendMessageToDiscussion';
import { SEND_MESSAGE_TO_DISCUSSION } from '../../graphql/discussion/mutation';
import { IMessage } from 'react-web-gifted-chat';
import { getUnseenMessageIds } from '../../utils/discussion';
import {
  Discussions,
  DiscussionsVariables,
} from '../../graphql/discussion/types/Discussions';
import {
  AllMessageUnseen,
  AllMessageUnseenVariables,
} from '../../graphql/discussion/types/AllMessageUnseen';
import { useSetDiscussionMessagesSeen } from '../../hooks/mutations/useSetMessageSeen';
import {
  DiscussionFilterContext,
  IDiscussionFilter,
} from '../../providers/DiscussionFilterProvider/DiscussionFilterProvider';
import { NEW_MESSAGE_TO_DISCUSSION } from '../../graphql/discussion/subscription';
import {
  NewMessageToDiscussion,
  NewMessageToDiscussionVariables,
} from '../../graphql/discussion/types/NewMessageToDiscussion';
import { Box, CircularProgress } from '@material-ui/core';

export type GiftedChatMessage = IMessage & {
  recordDirectoryToken: string;
  tags: (string | null)[] | null;
  isQuotation: boolean | null;
  quotedFrom: DiscussionMessages_discussionMessages_messages_quotedFrom | null;
  business: DiscussionMessages_discussionMessages_messages_business | null;
  isSeenByUsers:
    | (DiscussionMessages_discussionMessages_messages_isSeenByUsers | null)[]
    | null
    | undefined;
  discussionId?: number | null;
};

interface IDiscussionMessagesComponent {
  discussionId?: number;
  userIds?: number[];
}

const DiscussionMessage: FC<IDiscussionMessagesComponent> = (props) => {
  const { discussionId, userIds } = props;

  const client = useApolloClient();

  const snackBarData: SnackVariableInterface = {
    type: 'ERROR',
    message: '',
    isOpen: true,
  };

  const {
    me: { id: meId },
    canI,
  } = useContext<IUserContext>(UserContext);

  const { tags } = useContext<IDiscussionFilter>(DiscussionFilterContext);

  const canAnswer = canI(Operation.CREATE, Resource.DISCUSSIONS);

  const discussionMessagesVariables: DiscussionMessagesVariables =
    useMemo<DiscussionMessagesVariables>(
      () => ({ input: { discussionId, tags } }),
      [discussionId, tags],
    );

  const [setMessageSeen] = useSetDiscussionMessagesSeen();

  const { data: dataMessages, loading: isMessagesLoading } = useQuery<
    DiscussionMessages,
    DiscussionMessagesVariables
  >(GET_DISCUSSION_MESSAGE, {
    fetchPolicy: 'cache-and-network',
    variables: discussionMessagesVariables,
    onCompleted: (data) => {},
    onError: (error) => {
      snackBarData.message = "Une erreur inconnue s'est produite.";

      if (error.graphQLErrors) {
        if (error.graphQLErrors.length) {
          const currentError = error.graphQLErrors[0];

          if (currentError.extensions && currentError.extensions.code) {
            switch (currentError.extensions.code) {
              case ApiErrorCode.INTERNAL_SERVER_ERROR:
                snackBarData.message = 'Erreur interne du serveur.';
                break;

              case ApiErrorCode.INVALID_TOKEN:
              case ApiErrorCode.BAD_USER_INPUT:
                return;

              case ApiErrorCode.UNAUTHENTICATED:
              case ApiErrorCode.USER_NOT_FOUND:
                snackBarData.message =
                  'Vous devez vous connecter pour effectuer cette opération.';
                break;

              // case UKNOWN_ERROR:
              //   break;
            }
          }
        } else if (error.networkError) {
          snackBarData.message =
            'Erreur de réseau. Veuillez vérifier votre connexion internet.';
        }
      }

      displaySnackBar(client, snackBarData);
    },
  });

  const messages = useMemo<
    (DiscussionMessages_discussionMessages_messages | null)[] | null | undefined
  >(() => dataMessages?.discussionMessages?.messages, [dataMessages]);

  useEffect(() => {
    if ((dataMessages?.discussionMessages?.messages?.length ?? 0) !== 0) {
      const unseenMessageIds = getUnseenMessageIds(
        dataMessages?.discussionMessages?.messages,
      );

      // Set latest messages as "seen"
      if (discussionId) {
        if (unseenMessageIds?.length) {
          setMessageSeen({
            variables: {
              input: {
                discussionId,
                messageIds: unseenMessageIds,
              },
            },
          });
        }
      }

      // Set last message below discussion item as "seen"
      try {
        const currentDiscussions = client.readQuery<
          Discussions,
          DiscussionsVariables
        >({
          query: GET_DISCUSSIONS,
        });

        if (
          currentDiscussions?.discussions?.discussions &&
          currentDiscussions?.discussions?.discussions.length > 0
        ) {
          client.writeQuery<Discussions, DiscussionsVariables>({
            query: GET_DISCUSSIONS,
            data: Object.assign({}, currentDiscussions, {
              discussions: {
                __typename: currentDiscussions?.discussions?.__typename ?? '',
                discussions: currentDiscussions.discussions.discussions.map(
                  (d) => {
                    if (d?.id === discussionId) {
                      return {
                        ...d,
                        unseenMessageNumber: 0,
                        lastMessage: d?.lastMessage && {
                          ...d?.lastMessage,
                          isSeenByMe: true,
                        },
                      };
                    }
                    return d;
                  },
                ),
                totalCount:
                  (currentDiscussions?.discussions?.totalCount ?? 0) + 1,
              },
            }),
          });
        }
      } catch (e) {}

      // Update feedback notification badge number
      try {
        const currentAllMessageUnseen = client.readQuery<
          AllMessageUnseen,
          AllMessageUnseenVariables
        >({
          query: GET_ALL_MESSAGE_UNSEEN,
          variables: { input: { isBackoffice: true } },
        });

        const remainingMessageUnseen =
          (currentAllMessageUnseen?.allMessageUnseen ?? 0) -
          (unseenMessageIds?.length ?? 0);

        client.writeQuery<AllMessageUnseen>({
          query: GET_ALL_MESSAGE_UNSEEN,
          variables: { input: { isBackoffice: true } },
          data: {
            allMessageUnseen:
              remainingMessageUnseen > 0 ? remainingMessageUnseen : 0,
          },
        });
      } catch (e) {}
    }
  }, [dataMessages]);
  //subscription
  useSubscription<NewMessageToDiscussion, NewMessageToDiscussionVariables>(
    NEW_MESSAGE_TO_DISCUSSION,
    {
      variables: { discussionId: discussionId || 0 },
      onSubscriptionData: ({ client, subscriptionData }) => {
        const allMessages = client.readQuery<
          DiscussionMessages,
          DiscussionMessagesVariables
        >({
          query: GET_DISCUSSION_MESSAGE,
          variables: discussionMessagesVariables,
        });
        if (
          allMessages?.discussionMessages.messages?.length &&
          subscriptionData.data?.newMessageToDiscussion
        ) {
          client.writeQuery<DiscussionMessages, DiscussionMessagesVariables>({
            query: GET_DISCUSSION_MESSAGE,
            variables: discussionMessagesVariables,
            data: {
              discussionMessages: {
                ...allMessages.discussionMessages,

                messages: [
                  subscriptionData.data?.newMessageToDiscussion,
                  ...allMessages.discussionMessages.messages,
                ],
              },
            },
          });
        }
      },
    },
  );

  const [sendDiscussionMessageMutation, { loading: sendMessageLoading }] =
    useMutation<SendMessageToDiscussion, SendMessageToDiscussionVariables>(
      SEND_MESSAGE_TO_DISCUSSION,
      {
        onCompleted: (res) => {
          if (res?.sendMessageToDiscussion) {
            console.log('Message sent');
          }
        },
        onError: () => {
          // TODO : implement me
        },
        update: (client, { data }) => {
          if (data?.sendMessageToDiscussion) {
            // Update discussion messages
            const currentMessages = client.readQuery<
              DiscussionMessages,
              DiscussionMessagesVariables
            >({
              query: GET_DISCUSSION_MESSAGE,
              variables: discussionMessagesVariables,
            });
            // Update discussions
            const currentDiscussions = client.readQuery<
              Discussions,
              DiscussionsVariables
            >({
              query: GET_DISCUSSIONS,
            });

            client.writeQuery<Discussions, DiscussionsVariables>({
              query: GET_DISCUSSIONS,
              data: Object.assign({}, currentDiscussions, {
                discussions: {
                  ...currentDiscussions?.discussions,
                  discussions:
                    currentDiscussions?.discussions?.discussions?.map(
                      (discussion) =>
                        discussion?.id === discussionId
                          ? {
                              ...discussion,
                              lastMessage: data?.sendMessageToDiscussion,
                              lastMessageDate: new Date().toISOString(),
                            }
                          : discussion,
                    ),
                },
              }),
            });
          }
        },
      },
    );

  const allMessages: GiftedChatMessage[] = useMemo<GiftedChatMessage[]>(
    () =>
      messages?.map((message) => {
        return {
          id: message?.id ?? '',
          text: message?.content?.text ?? '',
          createdAt: new Date(message?.createdAt),
          user: {
            id: message?.sender?.id ?? -1,
            name: message?.sender?.firstName ?? '',
            avatar: message?.sender?.avatarUrl ?? '',
          },
          recordDirectoryToken: message?.recordDirectoryToken ?? '',
          tags: message?.tags ?? null,
          isQuotation: message?.isQuotation ?? false,
          quotedFrom: message?.quotedFrom ?? null,
          business: message?.business ?? null,
          isSeenByUsers: message?.isSeenByUsers,
          discussionId,
        };
      }) || [],
    [discussionId, messages],
  );

  const isFeedback = useMemo<boolean>(
    () => !!tags?.includes(DiscussionMessageTag.FEEDBACK),
    [tags],
  );

  const isQuotedFromFeeback = useCallback(
    (input: Partial<SendMessageToDiscussionInput> | undefined) =>
      allMessages?.findIndex(
        (m) =>
          m?.id === input?.quotedFromId &&
          m?.tags?.includes(DiscussionMessageTag.FEEDBACK),
      ) !== -1,
    [allMessages],
  );

  const handleSendMessageText = (
    input: Partial<SendMessageToDiscussionInput> | undefined,
  ) => {
    if ((input?.text?.length ?? 0) > 0 && meId) {
      sendDiscussionMessageMutation({
        variables: {
          input: {
            ...input,
            tags: [
              ...(isFeedback || isQuotedFromFeeback(input)
                ? [DiscussionMessageTag.FEEDBACK]
                : []),
              DiscussionMessageTag.CONNECTCREDIT,
            ],
            discussionId,
            userIds,
            contentType: DiscussionMessageType.TEXT,
          },
        },
      });
    }
  };

  return (
    <>
      {isMessagesLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexGrow={1}>
          <CircularProgress />
        </Box>
      ) : (
        <CustomChatRoom
          isLoadingNewMessage={isMessagesLoading}
          sendMessageLoading={sendMessageLoading}
          messages={allMessages}
          title=""
          onSendMessageText={handleSendMessageText}
          handleLoadEarlier={() => {}}
          loadEarlier={false}
          disabled={!canAnswer}
        />
      )}
    </>
  );
};

export default DiscussionMessage;
